import { useEffect, useRef } from "react";

export default function CtaEyes() {
  const eyesRef = useRef([]);
  const pupilsRef = useRef([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      eyesRef.current.forEach((eye, i) => {
        const pupil = pupilsRef.current[i];
        if (!eye || !pupil) return;
        const r = eye.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const d = Math.hypot(dx, dy) || 1;
        const m = Math.min(5, d / 20);
        eye.style.transform = `translate(${(dx / d) * m}px, ${(dy / d) * m}px)`;
      });
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    let raf;
    let stars = [];
    let last = 0;
    const resize = () => {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const spawn = () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 1.5 + Math.random() * 2;
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
      });
    };
    const step = (ts) => {
      if (ts - last > 700) { spawn(); last = ts; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars = stars.filter((s) => s.life > 0);
      for (const s of stars) {
        const tx = s.x - s.vx * 6;
        const ty = s.y - s.vy * 6;
        const grad = ctx.createLinearGradient(s.x, s.y, tx, ty);
        grad.addColorStop(0, `rgba(255,255,255,${(0.55 * s.life).toFixed(2)})`);
        grad.addColorStop(1, "rgba(255,255,255,0)");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.66;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(tx, ty);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(s.x, s.y, 1.8, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${(0.7 * s.life).toFixed(2)})`;
        ctx.fill();
        s.x += s.vx;
        s.y += s.vy;
        s.life -= 0.012;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" data-testid="proton-canvas" />
      <div className="hidden lg:flex relative z-10 shrink-0 self-center" data-testid="robot-eyes">
        <div className="bg-[#0B1220]/90 backdrop-blur rounded-2xl px-5 py-3 shadow-2xl border border-white/10 flex items-center justify-center gap-4">
          {[0, 1].map((i) => (
            <div key={i} ref={(el) => { eyesRef.current[i] = el; }} className="w-8 h-11 flex items-center justify-center">
              <div ref={(el) => { pupilsRef.current[i] = el; }}
                className="w-6 h-9 rounded-full bg-gradient-to-b from-[#7FF4FF] to-[#00CFEF] eye-glow eye-blink" />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
